import React from 'react'
import { useTranslation } from 'react-i18next'
import { FaMagic } from 'react-icons/fa'
import { FaClock } from 'react-icons/fa6'
import styled, { useTheme } from 'styled-components'
import { IBusinessOverviewRow } from '@query'
import { ColumnKey } from './columns'
import { MetricCell, Muted } from './RowCells'
import {
  automationColor,
  deadlineDaysLeft,
  formatDeadlineDate,
  formatHours,
  successDark,
  warningDark
} from './format'

// The deadline cell's relative line indents by dot + gap so it aligns with
// the label text above; all three measures derive from this one constant.
const DOT_SIZE_PX = 8

const MetricInline = styled.div<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  font-size: ${({ theme }) => theme.fontSize.px135}rem;
  font-weight: 700;
  color: ${({ $color }) => $color};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`

const DeadlineLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.metalGray};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const DeadlineDot = styled.span<{ $color: string }>`
  width: ${DOT_SIZE_PX}px;
  height: ${DOT_SIZE_PX}px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`

const DeadlineRel = styled.div<{ $color: string }>`
  font-size: ${({ theme }) => theme.fontSize.px115}rem;
  color: ${({ $color }) => $color};
  font-weight: 600;
  margin-top: ${({ theme }) => theme.spacing.xxs}rem;
  padding-left: ${DOT_SIZE_PX * 2}px;
`

// Deadline types the backend can emit; unknown types fall back to VAT.
const DEADLINE_TYPE_KEYS: Record<string, string> = {
  vat: 'businessesPage.table.deadlineVat',
  fiscal: 'businessesPage.table.deadlineFiscal'
}

const DeadlineCell: React.FC<{ row: IBusinessOverviewRow }> = ({ row }) => {
  const [t, i18n] = useTranslation()
  const theme = useTheme()
  const deadline = row.next_deadline
  // Null = this row's usership has no reporting permission.
  if (!deadline) {
    return <Muted>—</Muted>
  }
  const typeKey = DEADLINE_TYPE_KEYS[deadline.type || 'vat'] || DEADLINE_TYPE_KEYS.vat

  let dot = theme.colors.borderStrong
  let label: React.ReactNode = <Muted>—</Muted>
  let rel = t('businessesPage.table.deadlineNoFilings')
  let relColor = theme.colors.iconGray

  if (deadline.status === 'filed') {
    dot = theme.colors.success
    label = <DeadlineLabel>{t('businessesPage.table.vatFiled')}</DeadlineLabel>
    rel = t('businessesPage.table.deadlineFiled')
    relColor = successDark(theme)
  } else if (deadline.status === 'upcoming' && deadline.date) {
    const days = deadlineDaysLeft(row) ?? 0
    label = (
      <DeadlineLabel>
        {`${t(typeKey)} · ${formatDeadlineDate(deadline.date, i18n.language)}`}
      </DeadlineLabel>
    )
    // Same graded scale as the row tint: red within two days, warning
    // yellow within a week, nothing beyond that.
    if (days < 0) {
      dot = theme.colors.error
      rel = t('businessesPage.table.deadlineOverdue', { count: -days })
      relColor = theme.colors.error
    } else if (days === 0) {
      dot = theme.colors.error
      rel = t('businessesPage.table.deadlineToday')
      relColor = theme.colors.error
    } else if (days === 1) {
      dot = theme.colors.error
      rel = t('businessesPage.table.deadlineTomorrow')
      relColor = theme.colors.error
    } else if (days <= 2) {
      dot = theme.colors.error
      rel = t('businessesPage.table.deadlineInDays', { count: days })
      relColor = theme.colors.error
    } else if (days <= 7) {
      dot = theme.colors.warning
      rel = t('businessesPage.table.deadlineInDays', { count: days })
      relColor = warningDark(theme)
    } else {
      rel = t('businessesPage.table.deadlineInDays', { count: days })
    }
  }

  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: DOT_SIZE_PX }}>
        <DeadlineDot $color={dot} />
        {label}
      </div>
      <DeadlineRel $color={relColor}>{rel}</DeadlineRel>
    </div>
  )
}

const AutomationCell: React.FC<{ row: IBusinessOverviewRow }> = ({ row }) => {
  const theme = useTheme()

  if (row.automation_pct === null) {
    return <Muted>—</Muted>
  }
  return (
    <MetricInline $color={automationColor(row.automation_pct, theme)}>
      <FaMagic style={{ fontSize: `${theme.fontSize.px125}rem` }} />
      {`${row.automation_pct}%`}
    </MetricInline>
  )
}

const TimeSavedCell: React.FC<{ row: IBusinessOverviewRow }> = ({ row }) => {
  const [, i18n] = useTranslation()
  const theme = useTheme()

  if (row.time_saved_hours === null) {
    return <Muted>—</Muted>
  }
  return (
    <MetricInline $color={successDark(theme)}>
      <FaClock style={{ fontSize: `${theme.fontSize.px125}rem` }} />
      {formatHours(row.time_saved_hours, i18n.language)}
    </MetricInline>
  )
}

/** One data cell of the businesses table, shared between the full table
 * rows and the compact business-menu list. */
export const ColumnCell: React.FC<{ column: ColumnKey; row: IBusinessOverviewRow }> = ({
  column,
  row
}) => {
  switch (column) {
    case 'automation':
      return <AutomationCell row={row} />
    case 'deadline':
      return <DeadlineCell row={row} />
    case 'time_saved':
      return <TimeSavedCell row={row} />
    default:
      return <MetricCell column={column} row={row} />
  }
}
